// Where an invited crew member sets their password and lands in the app.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { useSession } from '../session';
import { AuthCard, Banner, Field } from '../ui';

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const token = params.get('token') ?? '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.acceptInvitation({
        token,
        password,
        ...(name.trim() === '' ? {} : { name: name.trim() }),
      });
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthCard title="Join your team on Dayboard">
        <Banner kind="error">
          That link is missing its code. Copy the whole link from the email, or ask whoever
          invited you to send another.
        </Banner>
        <p className="footnote">
          <Link to="/login">Already set up? Sign in</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Join your team on Dayboard">
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={submit} noValidate>
        <Field
          label="Your name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          hint="Leave blank to keep the name your workplace entered."
          autoComplete="name"
          required={false}
        />
        <Field
          label="Choose a password"
          name="password"
          type="password"
          value={password}
          onChange={setPassword}
          error={fields.password}
          hint="At least 10 characters."
          autoComplete="new-password"
          enterKeyHint="go"
        />
        <button className="btn-primary full" type="submit" disabled={busy}>
          {busy ? 'Setting up…' : 'Get started'}
        </button>
      </form>
    </AuthCard>
  );
}
